import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import _ from 'lodash';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import { Chart, ArgumentAxis, ValueAxis, Tooltip, LineSeries, ScatterSeries } from '@devexpress/dx-react-chart-material-ui';
import { EventTracker } from '@devexpress/dx-react-chart';
import CircularProgress from '@mui/material/CircularProgress';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { faChartArea } from '@fortawesome/pro-light-svg-icons';
import Header from '../Header';

const MONADA_AI = 'monada.ai';

function Stats() {
    const { rfqs } = useOutletContext();

    const [ collapsed, setCollapsed ] = useState([]);

    return (
        <Box sx={{ paddingBottom: '36px', background: '#fafbfc' }}>
            <Header
                icon={faChartArea}
                title={'Rates Procurement Dashboard'}
            />

            {!rfqs && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pt: 3 }}>
                    <CircularProgress />
                </Box>
            )}
            {rfqs && (
                <Box sx={{ p: 3, pt: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                        <Typography variant='h5'>
                            Shipments
                        </Typography>
                        <IconButton onClick={() => setCollapsed(_.xor(collapsed, ['shipments']))}>
                        {!collapsed.includes('shipments') ? <FontAwesomeIcon icon={faChevronDown} /> : <FontAwesomeIcon icon={faChevronRight} />}
                        </IconButton>
                    </div>
                    <Grid container spacing={3} sx={ collapsed.includes('shipments') ? { display: 'none' } : {} }>
                        <Grid item sm={6}>
                            <ShipmentsPerMonth
                                rfqs={rfqs}
                                types={['one-off', 'contract']}
                                title={'Shipments (Created) Per Month'}
                            />
                        </Grid>
                        <Grid item sm={6}>
                            <TopRoutes
                                rfqs={rfqs}
                            />
                        </Grid>
                        <Grid item sm={12}>
                            <ContainersPerMonth
                                rfqs={rfqs}
                            />
                        </Grid>
                    </Grid>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px', marginTop: '12px' }}>
                        <Typography variant='h5'>
                            Agents and Carriers
                        </Typography>
                        <IconButton onClick={() => setCollapsed(_.xor(collapsed, ['agents']))}>
                        {!collapsed.includes('agents') ? <FontAwesomeIcon icon={faChevronDown} /> : <FontAwesomeIcon icon={faChevronRight} />}
                        </IconButton>
                    </div>
                    <Grid container spacing={3} sx={ collapsed.includes('agents') ? { display: 'none' } : {} }>
                        <Grid item sm={6}>
                            <ShipmentsPerMonth
                                rfqs={rfqs}
                                types={['agent-query']}
                                title={'Rate Requests (Sent) Per Month'}
                            />
                        </Grid>
                        <Grid item sm={6}>
                            <QuotesPerMonth
                                data={rfqs}
                            />
                        </Grid>
                        <Grid item sm={6}>
                            <AverageQuotePerRfq
                                data={rfqs}
                            />
                        </Grid>
                        <Grid item sm={4}>
                            <TopSuppliersResponse
                                data={rfqs}
                                title={'Top 5 Suppliers by Response Rate'}
                                top
                            />
                        </Grid>
                        <Grid item sm={4}>
                            <TopSuppliersResponse
                                data={rfqs}
                                title={'Bottom 5 Suppliers by Response Rate'}
                            />
                        </Grid>
                    </Grid>
                </Box>
            )}
        </Box>
    );
}

function ShipmentsPerMonth({ rfqs, types, title }) {
    const [ showAxis, setShowAxis ] = useState(false);
    const [ mode, setMode ] = useState('table');

    useEffect(() => {
        setTimeout(() => setShowAxis(true), 1000);
        return () => {
            setShowAxis(false);
        }
    }, [ mode ]);

    const filtered = _.filter(rfqs, r => r.createdBy.indexOf(MONADA_AI) === -1 && types.includes(r.type) && r.status !== 'deleted');
    const grouped = _.groupBy(filtered, r => { const d = new Date(r.created); return d.getYear() * 12 + d.getMonth() });
    const months = _(grouped).keys().map(v => parseInt(v)).sort().value();

    const data = months.map(m => ({
        date: `${(m % 12 + 1)}/${Math.floor(m / 12) + 1900}`,
        count: types[0] !== 'agent-query' ? grouped[m].length : _.sumBy(grouped[m], r => r.suppliers.length),
    }));

    if (mode === 'chart') {
        return (
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant='body1' sx={{ fontWeight: '800', flex: 1 }}>
                        {title}
                    </Typography>
                    <Button disabled={ mode === 'table' } onClick={() => { setShowAxis(false); setMode('table')} }>Table</Button>
                    <Button disabled={ mode === 'chart' } onClick={() => { setShowAxis(false); setMode('chart')} }>Chart</Button>
                </Box>
                <Chart data={data} height={56 + 53 * _.keys(grouped).length}>
                    {showAxis && <ArgumentAxis />}
                    {showAxis && <ValueAxis />}
                    <ScatterSeries valueField='count' argumentField='date' />
                    <LineSeries valueField='count' argumentField='date' />
                    <EventTracker />
                    <Tooltip />
                </Chart>
            </Paper>
        )
    } else {
        return (
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant='body1' sx={{ fontWeight: '800', flex: 1 }}>
                        {title}
                    </Typography>
                    <Button disabled={ mode === 'table' } onClick={() => { setShowAxis(false); setMode('table')} }>Table</Button>
                    <Button disabled={ mode === 'chart' } onClick={() => { setShowAxis(false); setMode('chart')} }>Chart</Button>
                </Box>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    Month
                                </TableCell>
                                <TableCell>
                                    Shipments
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map(m => (
                                <TableRow key={m.date}>
                                    <TableCell>
                                        {m.date}
                                    </TableCell>
                                    <TableCell>
                                        {m.count}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        )
    }
}

function QuotesPerMonth({ data }) {
    const filtered = _.filter(data, d => d.createdBy.indexOf(MONADA_AI) === -1 && d.type === 'agent-query' && d.status !== 'deleted');
    const grouped = _.groupBy(filtered, d => { const date = new Date(d.created); return date.getYear() * 12 + date.getMonth() });
    const months = _(grouped).keys().map(v => parseInt(v)).sort().value();

    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant='body1' sx={{ fontWeight: '800' }}>
                Quotes Collected Per Month
            </Typography>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                Month
                            </TableCell>
                            <TableCell>
                                Quotes
                            </TableCell>
                            <TableCell>
                                # Suppliers
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {months.map(m => (
                            <TableRow key={m}>
                                <TableCell>
                                    {(m % 12) + 1}/{1900 + parseInt(m/12)}
                                </TableCell>
                                <TableCell>
                                    {_.sumBy(grouped[m], v => v.quotes[0].length)}
                                </TableCell>
                                <TableCell>
                                    {_.uniq(_.flatten(_.map(grouped[m], v => _.map(v.quotes[0], q => ((q.supplier || {}).organization || 'Unknown'))))).length}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    )
}

function ContainersPerMonth({ rfqs }) {
    const filtered = _.filter(rfqs, r => r.createdBy.indexOf(MONADA_AI) === -1 && (r.type === 'one-off' || r.type === 'contract') && r.status !== 'deleted');
    const grouped = _.groupBy(filtered, r => { const d = new Date(r.created); return d.getYear() * 12 + d.getMonth() });
    const months = _(grouped).keys().map(v => parseInt(v)).sort().value();
    const types = _(rfqs).map(r => r.products).flatten().map(r => r.type).uniq().sort().value();

    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant='body1' sx={{ fontWeight: '800' }}>
                Items Per Month
            </Typography>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                Month
                            </TableCell>
                            {types.map(t => (
                                <TableCell key={t}>
                                    {t}
                                </TableCell>
                            ))}
                            <TableCell>
                                <b>Total</b>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {months.map(m => (
                            <TableRow key={m}>
                                <TableCell>
                                    {(m % 12) + 1}/{1900 + parseInt(m/12)}
                                </TableCell>
                                {types.map(t => (
                                    <TableCell key={t}>
                                        {_(grouped[m]).map(r => r.products).flatten().filter(r => r.type === t).value().length}
                                    </TableCell>
                                ))}
                                <TableCell>
                                    <b>{_(grouped[m]).map(r => r.products).flatten().value().length}</b>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    )
}

function AverageQuotePerRfq({ data }) {
    const [ showAxis, setShowAxis ] = useState(false);
    const [ mode, setMode ] = useState('table');

    useEffect(() => {
        setTimeout(() => setShowAxis(true), 1000);
        return () => {
            setShowAxis(false);
        }
    }, [ mode ]);

    const filtered = _.filter(data, d => d.createdBy.indexOf(MONADA_AI) === -1 && d.type === 'agent-query' && d.status !== 'deleted');
    const grouped = _.groupBy(filtered, d => { const date = new Date(d.created); return date.getYear() * 12 + date.getMonth() });
    const months = _(grouped).keys().map(v => parseInt(v)).sort().value();

    const averages = months.map(m => ({
        date: `${(m % 12 + 1)}/${Math.floor(m / 12) + 1900}`,
        count: _.flatten(_.map(grouped[m], v => v.quotes)).length / grouped[m].length,
    }));

    if (mode === 'chart') {
        return (
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant='body1' sx={{ fontWeight: '800', flex: 1 }}>
                        Average Quotes Collected per Request
                    </Typography>
                    <Button disabled={ mode === 'table' } onClick={() => { setShowAxis(false); setMode('table')} }>Table</Button>
                    <Button disabled={ mode === 'chart' } onClick={() => { setShowAxis(false); setMode('chart')} }>Chart</Button>
                </Box>
                <Chart data={averages} height={56 + 53 * _.keys(grouped).length}>
                    {showAxis && <ArgumentAxis />}
                    {showAxis && <ValueAxis />}
                    <ScatterSeries valueField='count' argumentField='date' />
                    <LineSeries valueField='count' argumentField='date' />
                    <EventTracker />
                    <Tooltip />
                </Chart>
            </Paper>
        )
    } else {
        return (
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant='body1' sx={{ fontWeight: '800', flex: 1 }}>
                        Average Quotes per Request
                    </Typography>
                    <Button disabled={ mode === 'table' } onClick={() => { setShowAxis(false); setMode('table')} }>Table</Button>
                    <Button disabled={ mode === 'chart' } onClick={() => { setShowAxis(false); setMode('chart')} }>Chart</Button>
                </Box>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    Month
                                </TableCell>
                                <TableCell>
                                    Avg Quotes
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {averages.map(m => (
                                <TableRow key={m.date}>
                                    <TableCell>
                                        {m.date}
                                    </TableCell>
                                    <TableCell>
                                        {m.count.toFixed(2)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        )
    }
}

function TopRoutes({ rfqs }) {
    const filtered = _.filter(rfqs, r => r.createdBy.indexOf(MONADA_AI) === -1 && (r.type === 'one-off' || r.type === 'contract') && r.status !== 'deleted');
    const routes = _.countBy(_.flatten(filtered.map(f => f.routes.map(r => `${r.source.countryCode}|${r.destination.countryCode}`))));
    const sorted = _.sortBy(_.keys(routes), r => -1 * routes[r]);

    return (
        <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant='body1' sx={{ fontWeight: '800', flex: 1 }}>
                    Top 5 Routes
                </Typography>
            </Box>
            {sorted.slice(0, 5).map((r, i) => (
                <Box sx={{ pt: 1 }} key={r}>
                    {i + 1}. <b>{r.split('|')[0]} to {r.split('|')[1]}</b> <span style={{ opacity: 0.6, fontSize: '0.7em' }}>with {routes[r]} shipments ({(routes[r] * 100 / _.sum(_.values(routes))).toFixed(2)}%)</span>
                </Box>
            ))}
        </Paper>
    )
}

function TopSuppliersResponse({ data, title, top }) {
    const filtered = _.filter(data, d => d.createdBy.indexOf(MONADA_AI) === -1 && d.type === 'agent-query' && d.status !== 'deleted');
    const requested = filtered.map(d => ({ suppliers: d.suppliers.map(s => s.organization), quotes: d.quotes[0].map(s => ((s.supplier || {}).organization || 'Unknown')) }));
    const stats = {};
    _.each(requested, r => {
        _.each(r.suppliers, s => {
            stats[s] = stats[s] || [];
            if (r.quotes.includes(s)) {
                stats[s].push(1);
            } else {
                stats[s].push(0);
            }
        });
    });
    const sorted = _(stats).map((v, k) => ({ supplier: k, value: _.sum(v)/v.length, submitted: _.sum(v), count: v.length })).sortBy(v => ( top ? -1 : 1 ) * v.value).value();

    return (
        <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant='body1' sx={{ fontWeight: '800', flex: 1 }}>
                    {title}
                </Typography>
            </Box>
            {sorted.slice(0, 5).map((r, i) => (
                <Box sx={{ pt: 1 }} key={r}>
                    {i + 1}
                    .
                    &nbsp;
                    {r.supplier}
                    &nbsp;
                    (
                    {r.submitted}/{r.count} = {(r.value * 100).toFixed(2)}
                    %)
                </Box>
            ))}
        </Paper>
    )
}

export default Stats;
